import * as THREE from "three";
import { getClass, EFFECT_BIT, terrainHeight, terrainNormal } from "@tank/shared";
import type { AssetLoader } from "../assets/AssetLoader";
import type { TankPos } from "./SnapshotBuffer";
import type { Effects } from "./effects";

// Tread-dust tuning: only puff while actually moving at a reasonable clip,
// throttled so it reads as periodic tread kicks, not a continuous fog.
const DUST_MIN_SPEED = 1.5; // m/s
const DUST_INTERVAL_MS = 220;

// Scratch objects reused every frame (avoid per-tank/per-frame GC churn).
const _up = new THREE.Vector3(0, 1, 0);
const _normal = new THREE.Vector3();
const _tiltQuat = new THREE.Quaternion();
const _yawQuat = new THREE.Quaternion();
const _identityQuat = new THREE.Quaternion();
// Defense-in-depth: no matter how terrain tuning evolves, the hull should
// never visually over-rotate past a believable slope.
const MAX_TILT_RAD = THREE.MathUtils.degToRad(20);

// ── Modular power-up visuals ─────────────────────────────────────────────
// Each type gets its own icon shape + colored ground ring + its own bob/spin
// pace, so the three pickups read as distinct at a glance instead of the same
// bobbing blob in different colors.
interface PowerUpVisual {
  buildIcon: () => THREE.Object3D;
  ringColor: number;
  bobSpeed: number;
  spinSpeed: number;
}

function buildHealIcon(): THREE.Object3D {
  const g = new THREE.Group();
  const m = new THREE.MeshStandardMaterial({ color: 0x06d6a0, emissive: 0x06d6a0, emissiveIntensity: 0.6 });
  g.add(
    new THREE.Mesh(new THREE.BoxGeometry(0.3, 1.1, 0.3), m),
    new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.3, 0.3), m),
  );
  return g;
}

function buildSpeedIcon(): THREE.Object3D {
  return new THREE.Mesh(
    new THREE.ConeGeometry(0.5, 1.1, 6),
    new THREE.MeshStandardMaterial({ color: 0x3a86ff, emissive: 0x3a86ff, emissiveIntensity: 0.6 }),
  );
}

function buildRapidIcon(): THREE.Object3D {
  return new THREE.Mesh(
    new THREE.OctahedronGeometry(0.6),
    new THREE.MeshStandardMaterial({ color: 0xffbe0b, emissive: 0xffbe0b, emissiveIntensity: 0.6 }),
  );
}

function buildShieldIcon(): THREE.Object3D {
  const g = new THREE.Group();
  const m = new THREE.MeshStandardMaterial({ color: 0x9b5de5, emissive: 0x9b5de5, emissiveIntensity: 0.6 });
  const outer = new THREE.Mesh(new THREE.IcosahedronGeometry(0.62, 0), m);
  const innerMat = new THREE.MeshStandardMaterial({ color: 0x9b5de5, emissive: 0x9b5de5, emissiveIntensity: 0.9, wireframe: true });
  const inner = new THREE.Mesh(new THREE.IcosahedronGeometry(0.62, 0), innerMat);
  inner.scale.setScalar(1.18);
  g.add(outer, inner);
  return g;
}

const POWERUP_VISUALS: Record<string, PowerUpVisual> = {
  heal: { buildIcon: buildHealIcon, ringColor: 0x06d6a0, bobSpeed: 1.6, spinSpeed: 1.0 },
  speed: { buildIcon: buildSpeedIcon, ringColor: 0x3a86ff, bobSpeed: 2.4, spinSpeed: 2.2 },
  rapid: { buildIcon: buildRapidIcon, ringColor: 0xffbe0b, bobSpeed: 3.0, spinSpeed: 3.4 },
  shield: { buildIcon: buildShieldIcon, ringColor: 0x9b5de5, bobSpeed: 1.3, spinSpeed: 1.6 },
};

interface PowerUpMesh {
  group: THREE.Group;
  icon: THREE.Object3D;
  bobSpeed: number;
  spinSpeed: number;
}

function buildPowerUpMesh(type: string): PowerUpMesh {
  const visual = POWERUP_VISUALS[type] ?? POWERUP_VISUALS.heal;
  const group = new THREE.Group();
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(0.7, 0.06, 8, 24),
    new THREE.MeshStandardMaterial({ color: visual.ringColor, emissive: visual.ringColor, emissiveIntensity: 0.5 }),
  );
  ring.rotation.x = Math.PI / 2;
  ring.position.y = 0.05;
  group.add(ring);
  const icon = visual.buildIcon();
  icon.position.y = 1.1;
  group.add(icon);
  return { group, icon, bobSpeed: visual.bobSpeed, spinSpeed: visual.spinSpeed };
}

interface TankMesh {
  group: THREE.Group;
  turret: THREE.Object3D | null;
  shield: THREE.Mesh; // Bulwark bubble
  shimmer: THREE.Mesh; // Dash invuln phasing shell
  aura: THREE.Mesh; // Repair ground ring
}

interface TrackedProj {
  mesh: THREE.Mesh;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  t: number; // ms timestamp of last state update
}

export interface LocalProj {
  x: number;
  y: number;
  z: number;
}

interface UpdateArgs {
  remoteTanks: Map<string, TankPos>;
  localId: string;
  local: TankPos | null;
  projectiles: any; // room.state.projectiles (MapSchema) — remote only (own filtered)
  localProjectiles: LocalProj[]; // predicted, owned by the local player
  now: number;
}

/**
 * Manages tank + projectile meshes.
 * - Tanks: hand-built primitive, per-player color, separable turret.
 * - Remote projectiles: EXTRAPOLATED from last state position + velocity
 *   (constant velocity → exact, smooth despite the 20 Hz patch rate).
 * - Local projectiles: rendered directly (predicted client-side, no network lag).
 */
export class EntityManager {
  private tanks = new Map<string, TankMesh>();
  private projectiles = new Map<string, TrackedProj>();
  private localProj = new Map<number, THREE.Mesh>();
  private powerups = new Map<string, PowerUpMesh>();
  private prevPos = new Map<string, { x: number; z: number; t: number }>();
  private lastDust = new Map<string, number>();

  constructor(private scene: THREE.Scene, private assets: AssetLoader, private fx?: Effects) {}

  ensureTank(id: string, color: number, isLocal: boolean, classId: string = "balanced"): void {
    if (this.tanks.has(id)) return;
    const group = this.assets.getTank(color);
    const cls = getClass(classId);
    group.scale.setScalar(cls.scale * (isLocal ? 1.05 : 1));
    this.scene.add(group);

    // Ability-effect overlays (toggled each frame from the effect bitmask).
    const r = cls.radius;
    const shield = new THREE.Mesh(
      new THREE.SphereGeometry(r * 1.5, 16, 12),
      new THREE.MeshBasicMaterial({ color: 0x60a5fa, transparent: true, opacity: 0.18, side: THREE.DoubleSide, depthWrite: false }),
    );
    shield.position.y = 0.8;
    shield.visible = false;
    const shimmer = new THREE.Mesh(
      new THREE.IcosahedronGeometry(r * 1.4, 1),
      new THREE.MeshBasicMaterial({ color: 0xffffff, wireframe: true, transparent: true, opacity: 0.5, depthWrite: false }),
    );
    shimmer.position.y = 0.8;
    shimmer.visible = false;
    const aura = new THREE.Mesh(
      new THREE.RingGeometry(r * 0.9, r * 1.4, 28),
      new THREE.MeshBasicMaterial({ color: 0x34d399, transparent: true, opacity: 0.8, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false }),
    );
    aura.rotation.x = -Math.PI / 2;
    aura.position.y = 0.06;
    aura.visible = false;
    group.add(shield, shimmer, aura);

    this.tanks.set(id, { group, turret: group.getObjectByName("turret") ?? null, shield, shimmer, aura });
  }

  removeTank(id: string): void {
    const t = this.tanks.get(id);
    if (t) {
      this.scene.remove(t.group);
      t.group.traverse((o) => {
        if (o instanceof THREE.Mesh) {
          o.geometry.dispose();
          const m = o.material;
          if (Array.isArray(m)) m.forEach((mm) => mm.dispose());
          else m.dispose();
        }
      });
    }
    this.tanks.delete(id);
    this.prevPos.delete(id);
    this.lastDust.delete(id);
  }

  ensureProjectile(id: string, x: number, y: number, z: number, ricochet = false): void {
    if (this.projectiles.has(id)) return;
    const mesh = this.assets.getProjectile(ricochet);
    mesh.position.set(x, y, z);
    this.scene.add(mesh);
    this.projectiles.set(id, { mesh, x, y, z, vx: 0, vy: 0, vz: 0, t: 0 });
  }

  removeProjectile(id: string): void {
    const tp = this.projectiles.get(id);
    if (tp) {
      this.scene.remove(tp.mesh);
      tp.mesh.geometry.dispose();
      (tp.mesh.material as THREE.Material).dispose();
    }
    this.projectiles.delete(id);
  }

  // ── Power-ups ──
  ensurePowerUp(id: string, type: string, x: number, z: number, active: boolean): void {
    if (this.powerups.has(id)) return;
    const pu = buildPowerUpMesh(type);
    pu.group.position.set(x, 0, z);
    pu.group.visible = active;
    this.scene.add(pu.group);
    this.powerups.set(id, pu);
  }

  setPowerUpActive(id: string, active: boolean): void {
    const pu = this.powerups.get(id);
    if (pu) pu.group.visible = active;
  }

  removePowerUp(id: string): void {
    const pu = this.powerups.get(id);
    if (pu) {
      this.scene.remove(pu.group);
      pu.group.traverse((o) => {
        if (o instanceof THREE.Mesh) {
          o.geometry.dispose();
          (o.material as THREE.Material).dispose();
        }
      });
    }
    this.powerups.delete(id);
  }

  update(args: UpdateArgs): void {
    for (const [id, tm] of this.tanks) {
      const pos: TankPos | null = id === args.localId && args.local ? args.local : args.remoteTanks.get(id) ?? null;
      if (!pos) continue;
      // Cosmetic-only: the hull sits on + tilts with the terrain surface
      // (client rendering), while the authoritative XZ simulation never
      // touches elevation (see @tank/shared terrain.ts). Yaw is applied
      // first, then the whole hull is tilted to the local slope normal.
      const y = terrainHeight(pos.x, pos.z);
      tm.group.position.set(pos.x, y, pos.z);
      const n = terrainNormal(pos.x, pos.z);
      _normal.set(n.x, n.y, n.z);
      _tiltQuat.setFromUnitVectors(_up, _normal);
      const tiltAngle = 2 * Math.acos(Math.min(1, Math.abs(_tiltQuat.w)));
      if (tiltAngle > MAX_TILT_RAD) {
        _tiltQuat.slerp(_identityQuat, 1 - MAX_TILT_RAD / tiltAngle);
      }
      _yawQuat.setFromAxisAngle(_up, pos.rotY);
      tm.group.quaternion.copy(_tiltQuat).multiply(_yawQuat);
      if (tm.turret) tm.turret.rotation.y = pos.turretAngle - pos.rotY;
      tm.group.visible = pos.alive;
      // Ability overlays: only show while the matching effect bit is set.
      const e = pos.effects ?? 0;
      tm.shield.visible = pos.alive && !!(e & EFFECT_BIT.BULWARK);
      tm.shimmer.visible = pos.alive && !!(e & EFFECT_BIT.INVULN);
      tm.aura.visible = pos.alive && !!(e & EFFECT_BIT.REPAIRING);
      if (tm.shimmer.visible) tm.shimmer.rotation.y += 0.08; // gentle phasing spin

      // Tread dust: throttled puffs behind the tank while it's actually
      // moving at a real clip (avoids spawning particles for stationary
      // tanks or the sub-pixel jitter of render interpolation).
      if (this.fx && pos.alive) {
        const prev = this.prevPos.get(id);
        if (prev) {
          const dtS = (args.now - prev.t) / 1000;
          if (dtS > 0) {
            const speed = Math.hypot(pos.x - prev.x, pos.z - prev.z) / dtS;
            const last = this.lastDust.get(id) ?? 0;
            if (speed > DUST_MIN_SPEED && args.now - last > DUST_INTERVAL_MS) {
              const bx = pos.x - Math.sin(pos.rotY) * 1.6;
              const bz = pos.z - Math.cos(pos.rotY) * 1.6;
              this.fx.dustPuff(bx, terrainHeight(bx, bz) + 0.15, bz);
              this.lastDust.set(id, args.now);
            }
          }
        }
        this.prevPos.set(id, { x: pos.x, z: pos.z, t: args.now });
      }
    }

    // Remote projectiles: record latest state, then extrapolate by velocity.
    args.projectiles?.forEach((p: any, id: string) => {
      const tp = this.projectiles.get(id);
      if (!tp) return;
      tp.x = +p.x;
      tp.y = +p.y;
      tp.z = +p.z;
      tp.vx = +p.vx;
      tp.vy = 0;
      tp.vz = +p.vz;
      tp.t = args.now;
    });
    for (const tp of this.projectiles.values()) {
      const dt = (args.now - tp.t) / 1000;
      tp.mesh.position.set(tp.x + tp.vx * dt, tp.y + tp.vy * dt, tp.z + tp.vz * dt);
    }

    // Local predicted projectiles — render directly.
    const arr = args.localProjectiles ?? [];
    for (let i = 0; i < arr.length; i++) {
      let m = this.localProj.get(i);
      if (!m) {
        m = this.assets.getProjectile();
        this.scene.add(m);
        this.localProj.set(i, m);
      }
      m.position.set(arr[i].x, arr[i].y, arr[i].z);
    }
    for (const key of [...this.localProj.keys()]) {
      if (key >= arr.length) {
        const m = this.localProj.get(key)!;
        this.scene.remove(m);
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
        this.localProj.delete(key);
      }
    }

    // Power-ups: each type bobs/spins its icon at its own pace (ring stays
    // grounded) so the three pickups read as visually distinct, not just
    // recolored copies of the same shape.
    const t = args.now / 1000;
    for (const pu of this.powerups.values()) {
      if (!pu.group.visible) continue;
      pu.icon.position.y = 1.1 + Math.sin(t * pu.bobSpeed + pu.group.position.x) * 0.18;
      pu.icon.rotation.y = t * pu.spinSpeed;
    }
  }
}
